import React, { useState } from 'react';
import { storeGetOrDefault, storeSet, DEF_GEM } from '../../store';
import '../admin.css';

const Toast = ({ msg, type }) => msg ? <div className={`adm-toast adm-toast-${type}`}>{msg}</div> : null;

const emptyProduct = () => ({
  id: Date.now(),
  name: '',
  description: '',
  tdsLink: '',
  gemProductId: '',
  catalogId: '',
  gemLink: '',
});

const GemAdmin = () => {
  const [items, setItems] = useState(() => storeGetOrDefault('gem', DEF_GEM));
  const [toast, setToast] = useState({ msg: '', type: 'success' });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3000);
  };

  const save = (list) => {
    setItems(list);
    storeSet('gem', list);
    showToast('Saved successfully!');
  };

  const update = (idx, field, value) => {
    const list = items.map((it, i) => i === idx ? { ...it, [field]: value } : it);
    setItems(list);
  };

  const addProduct = () => setItems(prev => [...prev, emptyProduct()]);

  const removeProduct = (idx) => {
    if (!window.confirm('Remove this product row?')) return;
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const list = [...items];
    [list[idx - 1], list[idx]] = [list[idx], list[idx - 1]];
    setItems(list);
  };

  const moveDown = (idx) => {
    if (idx === items.length - 1) return;
    const list = [...items];
    [list[idx], list[idx + 1]] = [list[idx + 1], list[idx]];
    setItems(list);
  };

  const handleSave = () => {
    const invalid = items.find(it => !it.name.trim());
    if (invalid) return showToast('Each product must have a name.', 'error');
    save(items);
  };

  const handleReset = () => {
    if (!window.confirm('Reset all GeM products to default?')) return;
    setItems(DEF_GEM);
    storeSet('gem', DEF_GEM);
    showToast('Reset to defaults.');
  };

  const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: 7,
    border: '1px solid #e5e7eb', fontSize: '.82rem',
    background: '#fafafa', boxSizing: 'border-box',
  };

  return (
    <div className='adm-page'>
      <Toast {...toast} />

      <div className='adm-header'>
        <div>
          <h1 className='adm-title'>GeM Portal</h1>
          <p className='adm-subtitle'>Edit GeM product entries, links and IDs — changes sync live to the website.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className='adm-btn adm-btn-ghost' onClick={handleReset}>Reset Defaults</button>
          <button className='adm-btn adm-btn-primary' onClick={addProduct}>+ Add Product</button>
          <button className='adm-btn adm-btn-success' onClick={handleSave}>Save All</button>
        </div>
      </div>

      <div className='adm-card' style={{ padding: 0, overflowX: 'auto' }}>
        {items.map((item, idx) => (
          <div key={item.id || idx} style={{
            borderBottom: idx < items.length - 1 ? '1px solid #f0f2f5' : 'none',
            padding: '20px 24px',
          }}>
            {/* Row header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <span style={{ fontWeight: 700, fontSize: '.92rem', color: '#374151' }}>
                Product #{idx + 1}{item.name ? ` — ${item.name}` : ''}
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className='adm-order-btn' onClick={() => moveUp(idx)} disabled={idx === 0} title='Move Up'>▲</button>
                <button className='adm-order-btn' onClick={() => moveDown(idx)} disabled={idx === items.length - 1} title='Move Down'>▼</button>
                <button
                  className='adm-btn adm-btn-danger adm-btn-sm'
                  onClick={() => removeProduct(idx)}
                  style={{ marginLeft: 4 }}
                >Delete</button>
              </div>
            </div>

            {/* Fields grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 700, color: '#374151', marginBottom: 4 }}>
                  Product Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  style={inputStyle}
                  value={item.name}
                  onChange={e => update(idx, 'name', e.target.value)}
                  placeholder='e.g. Zinga - 1 kg Packing'
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 700, color: '#374151', marginBottom: 4 }}>Description</label>
                <input
                  style={inputStyle}
                  value={item.description}
                  onChange={e => update(idx, 'description', e.target.value)}
                  placeholder='Short product description'
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 700, color: '#374151', marginBottom: 4 }}>
                  GeM Product ID
                </label>
                <input
                  style={inputStyle}
                  value={item.gemProductId}
                  onChange={e => update(idx, 'gemProductId', e.target.value)}
                  placeholder='e.g. 5116877-96844560344'
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 700, color: '#374151', marginBottom: 4 }}>
                  Catalog ID
                </label>
                <input
                  style={inputStyle}
                  value={item.catalogId}
                  onChange={e => update(idx, 'catalogId', e.target.value)}
                  placeholder='e.g. 5116877-96844560344-cat'
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 700, color: '#374151', marginBottom: 4 }}>
                  GeM Link <span style={{ fontWeight: 400, color: '#9ca3af' }}>(shown as 🔗 in GeM Link column)</span>
                </label>
                <input
                  style={inputStyle}
                  value={item.gemLink}
                  onChange={e => update(idx, 'gemLink', e.target.value)}
                  placeholder='https://mkp.gem.gov.in/...'
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 700, color: '#374151', marginBottom: 4 }}>
                  TDS Link <span style={{ fontWeight: 400, color: '#9ca3af' }}>(Technical Data Sheet URL)</span>
                </label>
                <input
                  style={inputStyle}
                  value={item.tdsLink}
                  onChange={e => update(idx, 'tdsLink', e.target.value)}
                  placeholder='https://www.zinga.be/...'
                />
              </div>
            </div>
          </div>
        ))}

        {items.length === 0 && (
          <div className='adm-empty' style={{ padding: '48px 24px' }}>
            <div className='adm-empty-icon'>🏛️</div>
            <p>No GeM products found. Click "Add Product" to add one.</p>
          </div>
        )}
      </div>

      <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button className='adm-btn adm-btn-ghost' onClick={handleReset}>Reset Defaults</button>
        <button className='adm-btn adm-btn-success' onClick={handleSave}>Save All Changes</button>
      </div>
    </div>
  );
};

export default GemAdmin;
